// Módulo 3: bandeja de solicitudes y campañas con filtros por estado y semáforo.
import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import {
  Boton,
  EsqueletoTabla,
  Input,
  Pill,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  TituloPagina,
  Vacio,
} from "@iavmx/ui";
import { useBandeja, useConSesion, type FilaBandeja } from "./consultas";
import { Chip, Sem } from "./componentes";
import { ETIQUETA_SEMAFORO, fechaCorta, fechaLarga, relativo, tonoEstado, type Semaforo } from "./util";

const ORDEN_ESTADOS = ["Incompleta", "Por asignar", "Por desglosar", "En producción", "Asignada", "En revisión", "Publicada"];
const SEMAFOROS: Semaforo[] = ["en_riesgo", "atencion", "a_tiempo", "sin_fechas"];

function rutaDe(f: FilaBandeja) {
  return f.campanaId ? `/campanas/${f.campanaId}` : `/campanas/solicitudes/${f.solicitudId}`;
}

export function Bandeja() {
  const conSesion = useConSesion();
  const q = useBandeja();
  const navegar = useNavigate();
  const [estado, setEstado] = React.useState<string | null>(null);
  const [semaforo, setSemaforo] = React.useState<Semaforo | null>(null);
  const [busqueda, setBusqueda] = React.useState("");

  if (!conSesion) return <Vacio titulo="Entra para ver la bandeja" texto="Las solicitudes y campañas solo se ven con sesión." />;
  if (q.isLoading) return <EsqueletoTabla columnas={6} />;
  if (q.error) return <Vacio titulo="No se pudo cargar" texto={q.error.message} />;
  const d = q.data!;
  const texto = busqueda.trim().toLowerCase();
  const filas = d.filas.filter(
    (f) =>
      (!estado || f.estado === estado) &&
      (!semaforo || f.semaforo === semaforo) &&
      (!texto || `${f.titulo} ${f.areaSolicitante ?? ""} ${f.solicitante ?? ""}`.toLowerCase().includes(texto)),
  );
  const estados = [...new Set([...ORDEN_ESTADOS.filter((e) => d.conteos[e]), ...Object.keys(d.conteos)])];
  const enRiesgo = d.porSemaforo.en_riesgo;
  const nueva = (
    <Boton variante="acento" asChild>
      <Link to="/campanas/nueva">
        <Plus /> Nueva solicitud
      </Link>
    </Boton>
  );

  return (
    <>
      <TituloPagina
        descripcion={`${fechaLarga(d.hoy)} · ${d.total} ${d.total === 1 ? "solicitud abierta" : "solicitudes abiertas"}${enRiesgo ? `, ${enRiesgo} en riesgo de publicación` : ""}`}
        accion={nueva}
      >
        Solicitudes
      </TituloPagina>

      <div className="mb-4 grid gap-3">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-tenue" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Buscar campaña o solicitante"
            placeholder="Buscar campaña o solicitante"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="pl-9 text-base"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar por estado">
          <span className="mr-1 font-ui text-[11px] font-bold uppercase tracking-wide text-tinta-tenue">Estado</span>
          <Chip activo={estado === null} onClick={() => setEstado(null)} n={d.total}>
            Todas
          </Chip>
          {estados.map((e) => (
            <Chip key={e} activo={estado === e} onClick={() => setEstado(estado === e ? null : e)} n={d.conteos[e]}>
              {e === "Incompleta" ? "Incompletas" : e}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar por semáforo">
          <span className="mr-1 font-ui text-[11px] font-bold uppercase tracking-wide text-tinta-tenue">Semáforo</span>
          {SEMAFOROS.map((s) => (
            <Chip key={s} activo={semaforo === s} onClick={() => setSemaforo(semaforo === s ? null : s)} n={d.porSemaforo[s]}>
              {ETIQUETA_SEMAFORO[s]}
            </Chip>
          ))}
        </div>
      </div>

      {d.total === 0 ? (
        <Vacio
          titulo="Sin solicitudes todavía"
          texto="Pega el mensaje tal como llegó o llena el brief mínimo. Lo que llegue por WhatsApp, Slack o correo entra por aquí."
          accion={
            <Boton variante="primario" asChild>
              <Link to="/campanas/nueva">
                <Plus /> Nueva solicitud
              </Link>
            </Boton>
          }
        />
      ) : filas.length === 0 ? (
        <Vacio titulo="Sin coincidencias" texto="Ninguna solicitud cumple con esos filtros." accion={<Boton variante="contorno" onClick={() => { setEstado(null); setSemaforo(null); setBusqueda(""); }}>Quitar filtros</Boton>} />
      ) : (
        <>
          <p className="mb-2 font-ui text-xs text-tinta-tenue" aria-live="polite">
            {filas.length === d.total ? `${filas.length} en total` : `Mostrando ${filas.length} de ${d.total}`} · ordenadas por fecha de publicación
          </p>
          {/* Escritorio: tabla. */}
          <div className="hidden md:block">
            <Tabla>
              <TablaCabecera>
                <TablaFila>
                  <TablaEncabezado>Semáforo</TablaEncabezado>
                  <TablaEncabezado>Campaña o solicitud</TablaEncabezado>
                  <TablaEncabezado>Solicita</TablaEncabezado>
                  <TablaEncabezado>Publicación</TablaEncabezado>
                  <TablaEncabezado>Responsables</TablaEncabezado>
                  <TablaEncabezado>Pendiente ahora</TablaEncabezado>
                  <TablaEncabezado>Estado</TablaEncabezado>
                </TablaFila>
              </TablaCabecera>
              <TablaCuerpo>
                {filas.map((f) => (
                  <TablaFila key={f.solicitudId} className={f.semaforo === "en_riesgo" ? "bg-rojo/5" : undefined}>
                    <TablaCelda className="whitespace-nowrap align-top">
                      <Sem semaforo={f.semaforo} />
                    </TablaCelda>
                    <TablaCelda className="align-top">
                      <Link to={rutaDe(f)} className="font-semibold text-tinta hover:underline">
                        {f.titulo}
                      </Link>
                      <p className="mt-0.5 text-xs text-tinta-tenue">{f.motivo}</p>
                    </TablaCelda>
                    <TablaCelda className="align-top">
                      <p>{f.areaSolicitante ?? "—"}</p>
                      {f.solicitante && <p className="text-xs text-tinta-tenue">{f.solicitante}</p>}
                    </TablaCelda>
                    <TablaCelda className="whitespace-nowrap align-top tabular-nums">
                      <p>{fechaCorta(f.fechaPublicacion)}</p>
                      <p className="text-xs text-tinta-tenue">{relativo(d.hoy, f.fechaPublicacion)}</p>
                    </TablaCelda>
                    <TablaCelda className="align-top text-tinta-2">{f.responsables.length > 0 ? f.responsables.join(", ") : <span className="text-tinta-tenue">Sin asignar</span>}</TablaCelda>
                    <TablaCelda className="align-top">
                      {f.pendienteAhora ? (
                        <>
                          <p>{f.pendienteAhora.titulo}</p>
                          <p className="text-xs text-tinta-tenue">
                            {f.pendienteAhora.quien ?? "Sin responsable"} · {f.pendienteAhora.fechaLimite ? `vence ${fechaCorta(f.pendienteAhora.fechaLimite)}` : "sin fecha"}
                          </p>
                        </>
                      ) : f.camposFaltantes.length > 0 ? (
                        <p className="text-tinta-2">Completar el brief</p>
                      ) : f.campanaId ? (
                        <p className="text-tinta-2">Desglosar tareas</p>
                      ) : (
                        <p className="text-tinta-2">Armar la campaña</p>
                      )}
                    </TablaCelda>
                    <TablaCelda className="whitespace-nowrap align-top">
                      <Pill tono={tonoEstado(f.estado)}>{f.estado}</Pill>
                    </TablaCelda>
                  </TablaFila>
                ))}
              </TablaCuerpo>
            </Tabla>
          </div>
          {/* Celular: lista. */}
          <ul className="divide-y divide-linea border-y border-linea md:hidden">
            {filas.map((f) => (
              <li key={f.solicitudId}>
                <button
                  type="button"
                  className="flex w-full flex-col gap-1.5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => void navegar({ to: rutaDe(f) })}
                >
                  <div className="flex items-center gap-2">
                    <Sem semaforo={f.semaforo} />
                    <Pill tono={tonoEstado(f.estado)}>{f.estado}</Pill>
                  </div>
                  <p className="font-ui text-base font-semibold text-tinta">{f.titulo}</p>
                  <p className="font-ui text-sm text-tinta-tenue">
                    {f.motivo} · publica {fechaCorta(f.fechaPublicacion)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
